/**
 * Окно преобразования записи.
 *
 * Преобразование не делается молча по нажатию: `y = x² − 6x + 13` и
 * `y = (x − 3)² + 4` выглядят как две разные функции, и кривая, оставшаяся на
 * месте при полностью переписанной формуле, — главное, что здесь нужно
 * заметить. Поэтому сначала вопрос, в какой вид переходим.
 *
 * «Да с пояснением» — тот же переход, но с выкладкой на текущих числах
 * ученика. Отдельная кнопка, а не всегда раскрытое пояснение: кто уже умеет
 * выделять квадрат, не должен пролистывать шесть строк ради одного нажатия.
 */

import { useEffect, useId, useRef, useState } from "react";
import type { Conversion } from "../../lib/function-lab/conversion";

export function ConvertDialog({ conversion, onCancel, onConvert }: {
  conversion: Conversion;
  onCancel: () => void;
  /** Согласие: кривая переписывается, и ползунки под ней становятся другими. */
  onConvert: () => void;
}) {
  const [shown, setShown] = useState(false);
  const titleId = useId();
  const first = useRef<HTMLButtonElement>(null);

  // Фокус на первой кнопке и после раскрытия выкладки: окно модальное, и
  // клавиатура иначе остаётся в карточке под ним.
  useEffect(() => { first.current?.focus(); }, [shown]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onCancel();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onCancel]);

  const current = conversion.steps[0];

  return <div className="fn-lab__modal"
    onMouseDown={(event) => { if (event.target === event.currentTarget) onCancel(); }}>
    <div className="fn-lab__modal-box" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <h3 className="fn-lab__modal-title" id={titleId}>
        Преобразовать в вид{" "}
        <span className="fn-lab__modal-shape">{conversion.targetShape}</span>?
      </h3>
      {current ? <p className="fn-lab__modal-now">Сейчас: <b>{current.text}</b></p> : null}

      {shown ? <>
        <h4 className="fn-lab__modal-method">{conversion.method}</h4>
        <ol className="fn-lab__steps">
          {conversion.steps.map((step, index) => (
            <li key={index}>
              <span className="fn-lab__step-why">{step.why}</span>
              <b className="fn-lab__step-text">{step.text}</b>
            </li>
          ))}
        </ol>
      </> : null}

      <div className="fn-lab__modal-actions">
        {shown ? (
          <button ref={first} className="btn btn--primary" onClick={onConvert}>Преобразовать</button>
        ) : <>
          <button ref={first} className="btn btn--primary" onClick={onConvert}>Да</button>
          <button className="btn" onClick={() => setShown(true)}>Да с пояснением</button>
        </>}
        <button className="btn btn--quiet" onClick={onCancel}>Отмена</button>
      </div>
    </div>
  </div>;
}
